<template>
  <div class="flex-grow-1">

    <dialog-scroll-lock v-if="showForm" width="500" persistent :fullscreen="$vuetify.breakpoint.xsOnly" v-model="showForm">
      <form-builder v-model="order.event.form" @close="showForm = false" />
    </dialog-scroll-lock>

    <public-guest-list-dialog v-if="showGuestList" :form="order.event.form" :guests="guests" v-model="showGuestList" />

    <add-to-calendar-dialog v-if="showAddToCalendar" :event="order.event" v-model="showAddToCalendar" />

    <div class="fill-height overflow-auto relative opts-container" v-bind:class="{ 'hide-preview': hidePreview }">
      <title-toggle
        ref="details"
        title="Event name / details"
        :icon="mdiTextShort"
        :error="errors.name || errors.details"
        v-model="show"
      >
        <v-textarea
          class="white inline-error"
          outlined auto-grow rows="1" persistent-placeholder label="Event name *"
          :disabled="isCancelled"
          :rules="buildRules({ required: true, limit: 100 })"
          v-model="order.event.name"
          @update:error="v => errors.name = v"
        />
        <v-textarea
          class="mt-4 white inline-error"
          outlined auto-grow rows="3" placeholder="" persistent-placeholder label="Details"
          :disabled="isCancelled"
          :rules="buildRules({ limit: 500 })"
          v-model="order.event.details"
          @update:error="v => errors.details = v"
        />
      </title-toggle>

      <title-toggle
        ref="time-place"
        title="Time / place"
        :icon="mdiMapClock"
        :error="errors.time || errors.place"
        v-model="show"
      >
        <div class="d-flex flex-wrap ma-n2">
          <input-date
            class="ma-2"
            :label="`Date${isRequired('date_start') ? ' *' : ''}`"
            :clearable="!isRequired('date_start') || (order.state == 'PENDING')"
            :disabled="isCancelled"
            :rules="isRequired('date_start') ? [ v => !!v || 'Required' ] : []"
            v-model="order.event.date_start"
          />
          <div class="flex-grow-1 d-flex">
            <input-time
              class="ma-2 flex-grow-1"
              :label="`Start time${isRequired('time_start') ? ' *' : ''}`"
              :disabled="isCancelled"
              :rules="isRequired('time_start') ? [ v => !!v || 'Required' ] : []"
              v-model="order.event.time_start"
            />
            <input-time
              class="ma-2 flex-grow-1"
              label="End time"
              :disabled="isCancelled"
              :hint="(order.event.time_end && (order.event.time_end <= order.event.time_start)) ? '+1 day' : null"
              v-model="order.event.time_end"
            />
          </div>
        </div>
        <input-timezone
          :disabled="isCancelled"
          :datetime="order.event.from ? new Date(order.event.from) : (order.event.date_start ? new Date(order.event.date_start + 'T00:00:00') : null)"
          v-model="order.event.tz"
        />
        <event-location-form
          class="mt-n1"
          :required="isRequired('location')"
          :disabled="isCancelled"
          :event="order.event"
        />
      </title-toggle>

      <!--
      <title-toggle ref="host" title="Hosted by" :icon="mdiAccountStar" :error="errors.host" v-model="show">
        <v-textarea
          class="white inline-error"
          outlined auto-grow rows="1" persistent-placeholder
          :label="'Host name' + (multipleHosts ? 's' : '') + ' *'"
          :disabled="isCancelled"
          :rules="buildRules({ required: true, limit: 100 })"
          v-model="order.info.from"
          @update:error="v => errors.host = v"
        />
        <v-checkbox
          class="mt-4 pt-0"
          label="Multiple hosts" hide-details
          v-model="multipleHosts"
        />
        <div class="ml-8 text--label-active">Allow others to manage this invitation from their own Ellacard accounts</div>
      </title-toggle>
      -->

      <title-toggle v-if="!hideRsvpOptions" ref="rsvp" title="RSVP options" :icon="mdiTextBoxCheck" :error="errors.rsvp" v-model="show">
        <v-select
          class="white d-block"
          style="width: 432px; max-width: 100%;"
          dense outlined hide-details label="Allow responses from"
          :prepend-inner-icon="RESPONSE_OPTIONS.find(x => x.value == (isCancelled ? 'NONE' : (order.event.allowed || 'UNINVITED'))).icon"
          :disabled="isCancelled"
          :items="RESPONSE_OPTIONS"
          :value="isCancelled ? 'NONE' : (order.event.allowed || 'UNINVITED')"
          @input="v => $set(order.event, 'allowed', v)"
        >
          <template v-slot:item="{ item }">
            <div class="d-flex align-center py-2" style="max-width: 100%;">
              <v-icon class="mr-3" v-html="item.icon" />
              <div>
                <div style="line-height: 1.2em;" v-text="item.text" />
                <div class="text--label-active" v-text="item.details" />
              </div>
            </div>
          </template>
        </v-select>

        <v-checkbox
          class="mt-4 pt-0"
          label="Notify me when people RSVP" hide-details
          :disabled="isCancelled || (order.event.allowed == 'NONE')"
          :input-value="order.event.rsvp_notify && (order.event.allowed != 'NONE')"
          @change="v => $set(order.event, 'rsvp_notify', v)"
        />

        <v-checkbox
          class="mt-4 pt-0"
          label="Verify guest contact information" hide-details
          :disabled="isCancelled"
          v-model="order.event.verify"
        />
        <div class="ml-8 text--label-active">Prevents guests from replying using someone else's email address</div>

        <v-checkbox
          class="mt-4 pt-0"
          label="Make guest list public" hide-details
          v-model="order.event.public_guest_list"
        />
        <div class="ml-8 text--label-active">Email addresses and phone numbers are never visible to other guests</div>

        <v-expand-transition>
          <div v-if="order.event.public_guest_list">
            <v-radio-group class="pl-8 mt-2 pb-1" hide-details v-model="order.event.public_guest_list_mode">
              <v-radio label="Show all invited guests" value="ALL" />
              <v-radio label="Only show guests who responded" value="ALL_RSVP" />
              <v-radio label="Only show guests who can make it" value="ALL_RSVP_ATTENDING" />
            </v-radio-group>
          </div>
        </v-expand-transition>

        <template v-if="formRoot">
          <v-checkbox
            class="mt-4 pt-0"
            label="Limit RSVP party size" hide-details
            :disabled="isCancelled"
            :input-value="order.event.form.n_rsvp < 100"
            @change="v => order.event.form.n_rsvp = (v ? 1 : 100)"
          />
          <div class="ml-8 text--label-active">You can also adjust this property for individual <component :is="hidePreview ? 'span' : 'router-link'" :to="{ hash: '#delivery' }">invited guests</component></div>
          <int-field
            v-if="order.event.form.n_rsvp != 100"
            class="mt-2 ml-8"
            :disabled="isCancelled"
            :min="1"
            :max="99"
            :value="order.event.form.n_rsvp"
            @input="v => { if (v != 100) order.event.form.n_rsvp = v; }"
          />

          <v-checkbox
            class="mt-4 pt-0"
            label="Request separate counts of adults / children" hide-details
            :disabled="isCancelled"
            v-model="order.event.form.separate_counts"
          />

          <v-checkbox
            class="mt-4 pt-0"
            label="Request names of individual attendees" hide-details
            :disabled="isCancelled"
            v-model="order.event.form.request_names"
          />
        </template>

        <v-btn style="margin: 10px 0 -6px -16px;" text color="primary" :disabled="isCancelled" @click="showForm = true">
          <v-icon v-html="mdiTextBoxEdit" />
          <span class="ml-1 v-label" style="text-transform: none; letter-spacing: normal;">
            Customize RSVP form…
          </span>
        </v-btn>
      </title-toggle>

      <div v-if="$vuetify.breakpoint.mdAndDown && !hidePreview" style="height: 48px;" />
    </div>

    <div v-if="!hidePreview" class="elevation-20 absolute preview-container" v-bind:class="{ expanded: showPreview }">
      <v-btn
        class="hidden-lg-and-up elevation-20 absolute grey lighten-4 pa-6"
        style="right: 0; bottom: 100%; outline: thin solid rgba(0, 0, 0, 0.12);"
        text tile color="ec-dark-blue"
        @click="showPreview = !showPreview"
      >
        <v-icon
          class="ml-n1 mr-2"
          v-bind:style="{ transform: showPreview ? 'rotate(180deg)' : null }"
          small
          v-html="mdiArrowCollapseUp"
        />
        Preview RSVP form
      </v-btn>
      <div class="absolute-fill overflow-auto grey lighten-3" style="z-index: 1;">

        <v-btn v-blur class="mt-5 mb-7 mx-3" small color="primary" @click="$emit('preview')">
          <v-icon class="mr-1" small v-html="mdiAccountEye" />
          Preview / send test…
        </v-btn>

        <!--
        <v-list-item
          v-if="order.state != 'CANCELLED'"
          class="my-4 simple primary--text"
          :disabled="sendTestStatus.pending"
          @click="$emit('send-test')"
        >
          <v-progress-circular v-if="sendTestStatus.pending" indeterminate size="24" width="2" />
          <v-icon v-else v-html="mdiSend" />
          <div>Send yourself a test…</div>
        </v-list-item>
        -->

        <div class="text--label-active text-center">RSVP form preview</div>

        <div class="pt-4 pb-12 px-4">
          <v-form ref="test-rsvp" :key="testResetKey" @submit.prevent>
            <v-card class="py-5" tile>
              <event-summary
                class="px-6"
                :event="order.event"
                :totals="{ 'Attending': 11, 'Adults': 8, 'Children': 3 }"
                @click-calendar="showAddToCalendar = true"
                @click-guest-list="showGuestList = true"
              />
              <v-divider class="mt-5 mb-4" />
              <div class="px-6">
                <div class="title">Respond as</div>
                <v-text-field class="mt-1" dense hide-details outlined placeholder="Name" :rules="[ v => !!v ]" />
                <email-phone-field class="mt-3" required hide-contacts />
              </div>
            </v-card>
            <v-card class="mt-4 py-5 px-6" tile>
              <form-rsvp class="mt-n9" :form="order.event.form" :response="testResponse" />
            </v-card>
          </v-form>
          <div class="text-center mt-3">
            <v-btn
              class="mx-3"
              icon color="primary"
              @click="
                testResetKey = !testResetKey;
                testResponse = patchResponse(order.event.form, {});
              "
            >
              <v-icon v-html="mdiRefresh" />
              <span class="btn-tooltip bottom">Reset</span>
            </v-btn>
            <!--
            <v-btn class="mx-3" icon color="primary" @click="showFormRsvp = true">
              <v-icon v-html="mdiArrowExpandAll" />
              <span class="btn-tooltip bottom">Preview in fullscreen</span>
            </v-btn>
            -->
            <v-btn
              class="mx-3"
              icon color="primary"
              @click="$confirm({ ...($refs['test-rsvp'].validate() ? { title: 'No errors found', msg: 'Guests will be able to submit this response as is' } : { title: 'Errors found', msg: 'Guests will have to fix the highlighted sections before submitting a response' }), buttons: [ 'Ok' ], primary: 'Ok' })"
            >
              <v-icon v-html="mdiCheck" />
              <span class="btn-tooltip bottom">Test validation</span>
            </v-btn>
            <v-btn class="mx-3" icon color="primary" @click="showForm = true">
              <v-icon v-html="mdiPencil" />
              <span class="btn-tooltip bottom">Customize RSVP form</span>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.preview-container {
  z-index: 100;
  right: 0;
  bottom: 0;
  width: 400px;
  height: 0;
  max-width: 100%;
  border-top-left-radius: 4px;
  transition: height .3s;
}
.preview-container.expanded {
  height: min(680px, calc(100% - 80px));
  outline: thin solid rgba(0, 0, 0, 0.12);
}
.preview-container > * {
  border-top-left-radius: 4px;
}

@media (min-width: 1264px) {
  .preview-container,
  .preview-container.expanded {
    width: min(40%, 480px);
    height: 100%;
    outline: thin solid rgba(0, 0, 0, 0.12);
    border-top-left-radius: 0;
    box-shadow: none !important;
  }
  .preview-container > * {
    border-top-left-radius: 0;
  }
  .opts-container:not(.hide-preview) {
    margin-right: calc(1px + min(40%, 480px));
  }
}

.min-height-rows-2 .v-input__slot {
  min-height: 64px !important;
}

.to {
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 700;
  white-space: pre-wrap;
  line-height: 20px;
}

.guest-data {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  margin-top: 4px;
  margin-left: 16px;
  text-indent: -16px;
  line-height: 16px;
}

.to + .guest-data {
  margin-top: 2px;
}
</style>

<script>
import AddToCalendarDialog from '@/components/AddToCalendarDialog.vue';
import BtnSearch from '@/components/BtnSearch.vue';
import EmailPhoneField from '@/components/EmailPhoneField.vue';
import EventLocationForm from '@/components/EventLocationForm.vue';
import EventSummary from '@/components/EventSummary.vue';
import FormBuilder from '@/components/FormBuilder.vue';
import FormRsvp from '@/components/FormRsvp.vue';
import InputDate from '@/components/InputDate.vue';
import InputTime from '@/components/InputTime.vue';
import InputTimezone from '@/components/InputTimezone.vue';
import IntField from '@/components/IntField.vue';
import PublicGuestListDialog from '@/components/PublicGuestListDialog.vue';
import TitleToggle from '@/components/TitleToggle.vue';
import inputValidation from '@/mixins/input-validation.js';
import { isRequiredEventField, updateEventLinks } from '@/utils/event-links.js';
import { getTimezoneName, getDateStr, getTimeRangeStr, getUTCOffset, getISO } from '@/utils/time.js';
import { patchFormInputs, patchResponse } from '@/utils/event-rsvp-form.js';
import iconPathAccountEmail from 'raw-loader!@/assets/icon-path-account-email.txt';
import iconPathChild from 'raw-loader!@/assets/icon-path-child.txt';
import { mdiEye, mdiPencil, mdiMenuDown, mdiTextShort, mdiCalendar, mdiMapMarker, mdiAccountStar, mdiTextBoxCheck, mdiTextBoxEdit, mdiAccount, mdiAccountCheck, mdiLock, mdiMinus, mdiPlus, mdiMapClock, mdiArrowCollapseUp, mdiRefresh, mdiSend, mdiArrowExpandAll, mdiClose, mdiCheck, mdiHumanMale, mdiAccountEye } from '@mdi/js';


const RESPONSE_OPTIONS = [
  { text: 'Anyone', value: 'UNINVITED', details: 'Anyone with a link to your invitation can RSVP', icon: mdiAccount },
  // { text: 'Anyone with verified contact information', value: 'VERIFIED', details: "Prevents guests from replying using someone else's email address" , icon: mdiAccountCheck }, // Anyone with a link to your invitation can RSVP as long as they verify their contact information. If you invite guests by sharing a link, then this option adds a step in the RSVP process, but it ensures you will be able to send all guests follow-ups / reminders.
  { text: 'Invited guests only', value: 'INVITED', details: 'Only guests invited via email / text from Ellacard can RSVP', icon: iconPathAccountEmail },
  { text: 'No one', value: 'NONE', details: 'Disable all RSVPs', icon: mdiLock }
];


const GUEST_LIST = [
  { name: 'Abby', response: { 'Can you make it?': 'Yes', 'Total number of attendees': { adult: 1, child: 0 }, 'Attendees': [ { name: 'Abby', type: 'adult' } ] } },
  { name: 'Kevin & Sarah', response: { 'Can you make it?': 'Yes', 'Total number of attendees': { adult: 2, child: 0 }, 'Attendees': [ { name: 'Kevin', type: 'adult' }, { name: 'Sara', type: 'adult' } ] } },
  { name: 'The Johnson Family', response: { 'Can you make it?': 'Yes', 'Total number of attendees': { adult: 2, child: 2 }, 'Attendees': [ { name: 'Mark', type: 'adult'  }, { name: 'Emily', type: 'adult'  }, { name: 'Aaron', type: 'child'  }, { name: 'Jason', type: 'child' } ] } },
  { name: 'Michael', response: { 'Can you make it?': 'Yes', 'Total number of attendees': { adult: 1, child: 0 }, 'Attendees': [ { name: 'Michael', type: 'adult' } ] } },
  { name: 'William & Jessica', response: { 'Can you make it?': 'Yes', 'Total number of attendees': { adult: 2, child: 1 }, 'Attendees': [ { name: 'William', type: 'adult' }, { name: 'Jessica', type: 'adult' }, { name: 'Ogden', type: 'child' } ] } }
];


function buildDefaultSubject(name) {
  return `You're invited${name ? (': ' + name) : ''}`;
}


function buildEmptyPreview() {
  return {
    recipient: { to: 'Example recipient', email: 'example@example.com' },
    response: { 'Can you make it?': 'Yes' }
  };
}


export default {
  name: 'order-stepper-event',

  components: {
    AddToCalendarDialog,
    BtnSearch,
    EmailPhoneField,
    EventLocationForm,
    EventSummary,
    FormBuilder,
    FormRsvp,
    InputDate,
    InputTime,
    InputTimezone,
    IntField,
    PublicGuestListDialog,
    TitleToggle
  },

  mixins: [ inputValidation ],

  props: [ 'order', 'sendTestStatus', 'hidePreview', 'hideRsvpOptions' ],

  data() {
    return {
      errors: {
        name: false,
        details: false,
        time: false,
        place: false,
        // host: false,
        rsvp: false
      },

      show: [ 'Event name / details' ],
      showPreview: false,
      showForm: false,
      showAddToCalendar: false,
      showGuestList: false,
      filter: '',
      testResetKey: true,
      testResponse: patchResponse(this.order.event.form, {})
    };
  },

  computed: {
    datetime() {
      const { date_start, time_start, time_end, tz } = this.order.event;
      return [ date_start, time_start, time_end, tz ];
    },

    formRoot() {
      return this.order.event.form.inputs.find(x => x.root);
    },

    formOpts() {
      return _.pick(this.order.event.form, [ 'n_rsvp', 'separate_counts', 'request_names' ]);
    },

    isCancelled() {
      return this.order.state == 'CANCELLED';
    },

    isSent() {
      return !!this.order.send_started_at;
    },

    isDelayedSend() {
      const isSendAtInPast = this.order.info.send_at && (Date.parse(this.order.info.send_at) <= (new Date()));
      return !isSendAtInPast;
    },

    guests() {
      // Example guests for the public guest list
      const publicInputs = this.order.event.form.inputs.filter(x => x.public && ![ 'Can you make it?', 'Total number of attendees', 'Attendees' ].includes(x.title));
      return GUEST_LIST.map(
        x => {
          const copy = _.cloneDeep(x);
          for (const input of publicInputs) {
            if (input.type == 'RADIO')
              copy.response[input.title] = input.choices[ Math.floor(Math.random() * input.choices.length) ];
            else if (input.type == 'INT')
              copy.response[input.title] = input.min + Math.floor(Math.random() * (input.max - input.min));
          }
          return copy;
        }
      );
    }
  },

  created() {
    Object.assign(this, { RESPONSE_OPTIONS, patchResponse, iconPathChild, mdiEye, mdiPencil, mdiMenuDown, mdiTextShort, mdiCalendar, mdiMapMarker, mdiAccountStar, mdiTextBoxCheck, mdiTextBoxEdit, mdiMinus, mdiPlus, mdiMapClock, mdiArrowCollapseUp, mdiRefresh, mdiSend, mdiArrowExpandAll, mdiClose, mdiCheck, mdiHumanMale, mdiAccountEye });
  },

  async beforeRouteLeave(to, from, next) {
    next(false);
  },

  methods: {
    validate() {
      // Note that it's important to 'map' first to make sure every section gets validated
      return [ 'details', 'time-place', 'rsvp' ].map(x => this.$refs[x].$refs.form.validate()).every(x => x);
    },

    isRequired(field) {
      return isRequiredEventField(field, this.order);
    }
  },

  watch: {
    datetime([ date_start, time_start, time_end, tz ]) {
      const event = this.order.event;
      event.lz_tz_name = getTimezoneName(event.from, 'short', tz);

      event.from = getISO(date_start, time_start, tz);
      event.to = getISO(date_start, time_end, tz);
      if (event.from && event.to && (new Date(event.to) <= new Date(event.from))) {
        const toDate = new Date(event.to);
        toDate.setDate(toDate.getDate() + 1);
        event.to = toDate.toISOString();
      }

      event.lz_date = null;
      if (date_start)
        event.lz_date = getDateStr(new Date(date_start + ' ' + (time_start || '00:00:00')), tz);

      event.lz_time_range = null;
      if (time_start) {
        const fromDate = new Date(event.from || ('2020-01-01 ' + time_start));
        const toDate = event.to ? new Date(event.to) : null;
        event.lz_time_range = getTimeRangeStr(fromDate, toDate, tz);
      }
    },

    formOpts() {
      this.order.event.form.inputs = patchFormInputs(this.order.event.form);
      this.testResponse = patchResponse(this.order.event.form, this.testResponse);
    },

    'order.event.name'(v, prev) {
      if (this.order.skin.email_subject == buildDefaultSubject(prev))
        this.order.skin.email_subject = buildDefaultSubject(v);
    },

    'order.event': {
      deep: true,
      handler(v) {
        updateEventLinks(this.order, v);
      }
    }
  }
}
</script>
